import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInfo, useSnackbar } from '@c2fo/liquidity';
import { CircleCheckIcon } from '@c2fo/liquidity/icons';
import PreferredLogo from '@/enterprise/components/PreferredLogo';
import useSubmitInformationRequestPreferred from '@/enterprise/data/useSubmitInformationRequestPreferred';
import { useReporting } from '@/reporting';
import useSrm from '@/shared/data/useSrm';
import useUserDetails from '@/shared/data/useUserDetails';

interface PreferredMarketingContentProps {
  onRequestSubmitted?: () => void;
}

const PreferredMarketingContent = ({ onRequestSubmitted }: PreferredMarketingContentProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const srm = useSrm();
  const { data: userData } = useUserDetails();
  const showSnackbar = useSnackbar();
  const [showPreferredRequestModal, setShowPreferredRequestModal] = useState(false);
  const [preferredInfoRequestSubmitted, setPreferredInfoRequestSubmitted] = useState(false);
  const { mutateAsync: submitInformationRequestPreferred, isPending: isLoadingSubmitInformationRequestPreferred } =
    useSubmitInformationRequestPreferred();

  const sendPreferredInfoRequest = async () => {
    try {
      trackEnterpriseEvent('preferred-marketing-request::clicked', {
        srmEmail: srm.emailAddress,
        accountId: userData?.me?.account?.id,
        userEmail: userData?.me?.emailAddress,
      });
      await submitInformationRequestPreferred();
      setShowPreferredRequestModal(true);
      onRequestSubmitted?.();
    } catch {
      showSnackbar({
        message: t('core.error'),
      });
    } finally {
      setPreferredInfoRequestSubmitted(true);
    }
  };

  return (
    <>
      <Modal open={showPreferredRequestModal} onClose={() => setShowPreferredRequestModal(false)} size="sm">
        <ModalTitleInfo>{t('preferredMarketing.modal.requestSubmitted')}</ModalTitleInfo>
        <ModalContent center>
          <CircleCheckIcon className="mx-auto mb-3 h-[70px] w-auto" />
          <p>{t('preferredMarketing.modal.requestSubmitted.description')}</p>
        </ModalContent>
        <ModalActions>
          <Button variant="secondary" onClick={() => setShowPreferredRequestModal(false)}>
            {t('core.close')}
          </Button>
        </ModalActions>
      </Modal>

      <div className="p-4">
        <div className="mx-auto max-w-[224px]">
          <PreferredLogo />
        </div>
        <div className="mx-auto max-w-2xl text-center">
          <h4>{t('preferredMarketing.table.title')}</h4>
          <p>{t('preferredMarketing.table.description')}</p>
          <p className="my-6 text-left">{t('preferredMarketing.table.benefits.header')}</p>
          <ul className="ml-7 list-disc text-left">
            <li>
              <span className="font-semibold">{t('preferredMarketing.table.benefits.peaceOfMind')} </span>
              {t('preferredMarketing.table.benefits.peaceOfMind.description')}
            </li>
            <li>
              <span className="font-semibold">{t('preferredMarketing.table.benefits.cashFlow')} </span>
              {t('preferredMarketing.table.benefits.cashFlow.description')}
            </li>
            <li>
              <span className="font-semibold">{t('preferredMarketing.table.benefits.simplicity')} </span>
              {t('preferredMarketing.table.benefits.simplicity.description')}
            </li>
          </ul>
          <Button
            size="md"
            variant="secondary"
            className="mx-auto my-6 w-full md:w-fit"
            onClick={sendPreferredInfoRequest}
            loading={isLoadingSubmitInformationRequestPreferred}
            disabled={isLoadingSubmitInformationRequestPreferred || preferredInfoRequestSubmitted}
          >
            {t('preferredMarketing.table.requestInformation')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PreferredMarketingContent;
